import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { X, Home, Users, Calendar, MapPin, BookOpen, User, BarChart3, Bell, Map, Shield, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import StudyShareModal from '../studies/StudyShareModal';
import { APP_VERSION } from '@/config/version';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Navigation items - estrutura exata do menu original
  interface NavigationItem {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    roles: string[];
    iconOnly?: boolean;
  }

  const navigationItems: NavigationItem[] = [
    { name: 'Home', href: '/', icon: Home, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Contatos', href: '/contacts', icon: Users, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Endereços', href: '/addresses', icon: MapPin, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Agenda', href: '/agenda', icon: Calendar, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Equipes', href: '/teams', icon: Users, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Estudos', href: '/studies', icon: BookOpen, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Regionais', href: '/regions', icon: Map, roles: ['admin'] },
    { name: 'Usuários', href: '/users', icon: User, roles: ['admin','leader', 'missionary'] },
    { name: 'Relatórios', href: '/reports', icon: BarChart3, roles: ['admin', 'leader'] },
    { name: 'Notificações', href: '/notifications', icon: Bell, roles: ['admin', 'leader', 'missionary'] },
    { name: 'Permissões', href: '/admin/permissions', icon: Shield, roles: ['admin'] },
    { name: 'SysInfo', href: '/sysinfo', icon: Shield, roles: ['admin'] },
  ];

  // Filtrar navegação baseado no role do usuário
  const filteredNavigation = navigationItems.filter(item => 
    user?.role && item.roles.includes(user.role as any)
  );

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-gray-600 dark:bg-black bg-opacity-75 z-40 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-lg transform transition-transform duration-300 ease-in-out',
        'lg:relative lg:translate-x-0 lg:z-auto',
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">IDE</span>
              </div>
              <h1 className="ml-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Missionários</h1>
            </div>
            <button type="button" className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>



          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={({ isActive }) => cn(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-r-2 border-blue-500 dark:border-blue-600'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                  )}
                >
                  {item.name === 'Estudos' ? (
                    // Icon acts as a trigger to open WhatsApp share modal
                    <button
                      type="button"
                      className="mr-3 h-5 w-5 flex-shrink-0 text-current hover:text-blue-700 focus:outline-none"
                      onClick={(e) => {
                        // prevent NavLink navigation; only open modal
                        e.preventDefault();
                        e.stopPropagation();
                        setShareModalOpen(true);
                      }}
                      title="Enviar estudo por WhatsApp"
                      aria-label="Enviar estudo por WhatsApp"
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  ) : item.iconOnly ? (
                    // Icon-only entry: show only the icon (e.g., messaging shortcut)
                    <button
                      type="button"
                      className="mr-0 h-5 w-5 flex-shrink-0 text-current hover:text-blue-700 focus:outline-none"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Navigate to users page (could be enhanced to open compose directly)
                        window.location.href = '/users';
                      }}
                      title={item.name}
                      aria-label={item.name}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  ) : (
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  )}
                  {!item.iconOnly && item.name}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {/* Realtime mode indicator (before version) */}
            {(() => {
              const wsEnabled = (() => {
                // localStorage overrides
                try {
                  const ls = localStorage.getItem('pulse.ws.enabled');
                  if (ls != null) return !(ls === '0' || ls.toLowerCase() === 'false');
                } catch { /* ignore */ }
                // Vite env
                const vite = (import.meta as any)?.env?.VITE_PULSE_WS_ENABLED as string | undefined;
                if (typeof vite === 'string') return !(vite === '0' || vite.toLowerCase() === 'false');
                // Runtime window.__ENV
                try {
                  const runtime = (window as any)?.__ENV?.PULSE_WS_ENABLED as string | boolean | undefined;
                  if (typeof runtime === 'string') return !(runtime === '0' || runtime.toLowerCase() === 'false');
                  if (typeof runtime === 'boolean') return runtime;
                } catch { /* ignore */ }
                return true; // default enabled
              })();
              return (
                <div className="mb-2 text-[11px] text-center">
                  <div className="flex items-center gap-1">
                    <Wifi className={`h-3.5 w-3.5 ${wsEnabled ? 'text-green-600' : 'text-amber-600'}`} />
                    <span className={`inline-flex items-center rounded px-2 py-0.5 ${wsEnabled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                          title={wsEnabled ? 'Realtime via WebSocket' : 'Realtime via HTTP long-polling'}>
                      {wsEnabled ? 'Realtime: WS' : 'Realtime: Polling'}
                    </span>
                  </div>
                </div>
              );
            })()}
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">IDE Missionários
              <span id="frontend-version" className="ml-2 px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs" title="Frontend version">{APP_VERSION}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Modal for sharing studies via WhatsApp */}
      <StudyShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} />
    </>
  );
};

export default Sidebar;